import { Link } from "react-router-dom";
import { ClipboardList, Phone, Mail, ArrowDown, ArrowUpRight } from "lucide-react";
import { FadeUp, Eyebrow, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import GiftEditForm from "@/components/forms/GiftEditForm";
import { IMAGES, CONTACT } from "@/data/products";

const PATHWAYS = [
  {
    icon: ClipboardList,
    title: "Easy Edit",
    subtitle: "Complete our online brief",
    desc: "A curated questionnaire that takes about three minutes. Tell us about them in your own time, and we'll take it from there.",
    cta: "Start the brief",
    href: "#brief",
    testid: "gift-edit-pathway-easy",
  },
  {
    icon: Phone,
    title: "Consultation Edit",
    subtitle: "Speak with a curator",
    desc: "Prefer to talk it through? Call us and we'll shape the brief together — ideal for milestone occasions and bigger budgets.",
    cta: CONTACT.phone,
    href: `tel:${CONTACT.phone.replace(/\s/g, "")}`,
    testid: "gift-edit-pathway-consultation",
  },
  {
    icon: Mail,
    title: "Personal Edit",
    subtitle: "Email or text us directly",
    desc: "Send your notes, photos, voice memos or half-formed ideas straight to us. We'll reply personally and build from there.",
    cta: CONTACT.email,
    href: `mailto:${CONTACT.email}?subject=Personal%20Edit%20—%20Gift%20Brief`,
    testid: "gift-edit-pathway-personal",
  },
];

const PROCESS = [
  { n: "01", title: "You brief us", text: "The recipient, their world, the occasion and your budget — in whichever way suits you." },
  { n: "02", title: "We propose & agree", text: "A curated proposal with sourcing notes. The price is discussed and agreed before any payment." },
  { n: "03", title: "We source & assemble", text: "Every item hand-chosen, quality-checked and composed into a single, coherent gift." },
  { n: "04", title: "Wrapped & delivered", text: "Signature wrapping, a handwritten note, and tracked delivery to you or straight to them." },
];

export default function GiftEdit() {
  return (
    <div data-testid="gift-edit-page">
      <section className="relative pt-40 pb-20 lg:pb-28 spotlight overflow-hidden">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <FadeUp><Eyebrow>The Gift Edit — Bespoke Gifts</Eyebrow></FadeUp>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-chocolate leading-[1.08] tracking-tight mt-6">
              <MaskedLine delay={0.1}>Crafted solely</MaskedLine>
              <MaskedLine delay={0.25} className="italic text-golddark">for them.</MaskedLine>
            </h1>
            <FadeUp delay={0.35}>
              <p className="text-base md:text-lg text-chocolate/65 leading-relaxed max-w-xl mt-7">
                Tell us about the recipient — their interests, personality, the occasion and your budget. We personally source, curate, assemble and wrap the perfect gift, so it feels like you thought of everything. Because you did.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.25} className="lg:col-span-5 hidden lg:block">
            <div className="relative aspect-[4/3] overflow-hidden frame-gold">
              <img src={IMAGES.bespoke} alt="A bespoke gift edit being composed" className="img-warm w-full h-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>

      <Marquee />

      {/* PATHWAYS */}
      <section className="py-20 lg:py-28 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <FadeUp>
          <Eyebrow>Three ways to work with us</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-chocolate mt-5 leading-[1.15]">
            Choose how you'd like <span className="italic text-golddark">to brief us.</span>
          </h2>
        </FadeUp>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {PATHWAYS.map((p, i) => (
            <FadeUp key={p.title} delay={i * 0.12} className="h-full">
              <div data-testid={p.testid}
                className="group h-full bg-ivory border border-chocolate/10 hover:border-gold/70 p-8 lg:p-10 flex flex-col transition-all duration-500 hover:shadow-[0_24px_50px_-28px_rgba(26,15,10,0.3)]">
                <p.icon size={26} strokeWidth={1.4} className="text-golddark" />
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-golddark mt-8">{p.subtitle}</p>
                <h3 className="font-serif text-3xl font-light text-chocolate mt-2">{p.title}</h3>
                <p className="text-sm text-chocolate/60 leading-relaxed mt-4 flex-1">{p.desc}</p>
                {p.href.startsWith("#") ? (
                  <a href={p.href} data-testid={`${p.testid}-cta`}
                    className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-chocolate border-b border-gold/60 pb-1 self-start group-hover:gap-3.5 transition-all">
                    {p.cta} <ArrowDown size={13} />
                  </a>
                ) : (
                  <a href={p.href} data-testid={`${p.testid}-cta`}
                    className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-chocolate border-b border-gold/60 pb-1 self-start group-hover:gap-3.5 transition-all break-all">
                    {p.cta} <ArrowUpRight size={13} className="shrink-0" />
                  </a>
                )}
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 lg:py-28 bg-ivory border-y border-chocolate/10">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
          <FadeUp>
            <Eyebrow>The process</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-chocolate mt-5 leading-[1.15]">
              From brief <span className="italic text-golddark">to beautifully wrapped.</span>
            </h2>
          </FadeUp>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mt-16">
            {PROCESS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.1}>
                <div data-testid={`process-step-${i + 1}`} className="border-t-2 border-gold/60 pt-7">
                  <span className="font-serif text-5xl font-light text-gold/60">{s.n}</span>
                  <h3 className="font-serif text-xl text-chocolate mt-4">{s.title}</h3>
                  <p className="text-sm text-chocolate/60 mt-3 leading-relaxed">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <div className="mt-14 bg-cream border border-gold/50 p-6 lg:p-8 flex flex-col md:flex-row md:items-center gap-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-golddark shrink-0">A note on pricing</p>
              <p className="text-sm text-chocolate/70 leading-relaxed">
                There are no fixed prices for bespoke edits — every gift is composed individually. We discuss your requirements, present a proposal, and agree the price with you before any payment is taken.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* BRIEF FORM */}
      <section id="brief" className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto scroll-mt-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <FadeUp>
              <Eyebrow>The Easy Edit brief</Eyebrow>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-chocolate mt-5 leading-[1.15]">
                Three minutes of you, <span className="italic text-golddark">a lifetime impression for them.</span>
              </h2>
              <p className="text-sm text-chocolate/60 leading-relaxed mt-6">
                Answer as much or as little as you like — the colour you give us is what makes the gift unmistakably theirs. A curator reads every brief personally.
              </p>
              <ul className="mt-8 space-y-3">
                {["Read personally by a curator", "Proposal & price agreed before payment", "Hand-wrapped in our signature style"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-sm text-chocolate/70">
                    <span className="w-1.5 h-1.5 rotate-45 bg-gold shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
          <div className="lg:col-span-8">
            <FadeUp delay={0.15}>
              <GiftEditForm />
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
